<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import { listSiteDiaries, listWeeklyReports, pushEvents } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data: reports, isPending, isError, error } = useQuery({
  queryKey: ["weekly-reports", selectedProjectId],
  queryFn: () => listWeeklyReports(selectedProjectId.value),
});
const { data: diaries } = useQuery({
  queryKey: ["site-diaries", selectedProjectId],
  queryFn: () => listSiteDiaries(selectedProjectId.value),
});

// Distinct projects, derived from the projects that have site diaries.
const projects = computed(() => {
  const map = new Map<string, string>();
  for (const d of diaries.value ?? []) map.set(d.project_id, d.project_name);
  return [...map.entries()].map(([id, name]) => ({ id, name }));
});

const form = ref({
  projectId: "",
  weekLabel: "",
  periodStart: "",
  periodEnd: "",
  preparedBy: "",
});
const creating = ref(false);
const formError = ref<string | null>(null);

async function create(): Promise<void> {
  const f = form.value;
  if (!f.projectId || !f.weekLabel.trim() || !f.periodStart || !f.periodEnd) {
    formError.value = "Project, week label and both dates are required.";
    return;
  }
  creating.value = true;
  formError.value = null;
  try {
    const id = crypto.randomUUID();
    const projectName =
      projects.value.find((p) => p.id === f.projectId)?.name ?? "";
    await pushEvents([
      {
        aggregate_type: "weekly_report",
        aggregate_id: id,
        event_type: "WeeklyReportCreated",
        payload: {
          project_id: f.projectId,
          project_name: projectName,
          week_label: f.weekLabel.trim(),
          period_start: f.periodStart,
          period_end: f.periodEnd,
          prepared_by: f.preparedBy.trim() || undefined,
        },
      },
    ]);
    await queryClient.invalidateQueries({ queryKey: ["weekly-reports"] });
    await router.push({
      name: "weekly-report-detail",
      params: { projectId: selectedProjectId.value, id },
    });
  } catch (e) {
    formError.value = e instanceof Error ? e.message : String(e);
  } finally {
    creating.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <div class="grid gap-8">
      <header>
        <h1 class="text-2xl font-extrabold text-neutral-900">
          Weekly project reports
        </h1>
        <p class="mt-1 text-neutral-500">
          The management rollup above the daily site diary — the factual
          sections are generated from the week's diaries.
        </p>
      </header>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-4 font-semibold">
          New weekly report
        </h2>
        <p
          v-if="!projects.length"
          class="text-sm text-neutral-500"
        >
          No projects yet — a report is built from a project's site diaries,
          so create some diaries first.
        </p>
        <div
          v-else
          class="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
        >
          <label class="grid gap-1 text-sm lg:col-span-2">
            <span class="text-neutral-500">Project</span>
            <select
              v-model="form.projectId"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
              <option value="">
                Select…
              </option>
              <option
                v-for="p in projects"
                :key="p.id"
                :value="p.id"
              >
                {{ p.name }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Week label</span>
            <input
              v-model="form.weekLabel"
              type="text"
              placeholder="WK06"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Period start</span>
            <input
              v-model="form.periodStart"
              type="date"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Period end</span>
            <input
              v-model="form.periodEnd"
              type="date"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm lg:col-span-2">
            <span class="text-neutral-500">Prepared by (optional)</span>
            <input
              v-model="form.preparedBy"
              type="text"
              placeholder="Name / team"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
        </div>
        <p
          v-if="formError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ formError }}
        </p>
        <button
          v-if="projects.length"
          type="button"
          class="mt-4 rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
          :disabled="creating"
          @click="create"
        >
          {{ creating ? "Creating…" : "Create report" }}
        </button>
      </section>

      <div
        v-if="isPending"
        class="text-neutral-500"
      >
        Loading…
      </div>
      <div
        v-else-if="isError"
        class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
      >
        Failed to load: {{ error?.message }}
      </div>
      <div
        v-else-if="reports"
        class="grid gap-3"
      >
        <p
          v-if="!reports.length"
          class="text-neutral-500"
        >
          No weekly reports yet.
        </p>
        <RouterLink
          v-for="r in reports"
          :key="r.id"
          :to="{ name: 'weekly-report-detail', params: { projectId: selectedProjectId, id: r.id } }"
          class="rounded-xl border border-neutral-200 bg-neutral-0 p-5 transition-shadow hover:shadow-md"
        >
          <h3 class="font-semibold">
            {{ r.project_name }} — {{ r.week_label }}
          </h3>
          <p class="mt-1 text-sm text-neutral-500">
            {{ r.period_start }} to {{ r.period_end }}
          </p>
        </RouterLink>
      </div>
    </div>
  </PageLayout>
</template>
